import { AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * Frontend password rules – instant feedback only.
 * The ENFORCED copy lives in functions/src/passwordPolicy.ts (registerUser),
 * together with the breached-password check. Keep the two in sync.
 */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordRule {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  { id: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: p => p.length >= PASSWORD_MIN_LENGTH && p.length <= PASSWORD_MAX_LENGTH },
  { id: 'lower', label: 'A lowercase letter', test: p => /[a-z]/.test(p) },
  { id: 'upper', label: 'An uppercase letter', test: p => /[A-Z]/.test(p) },
  { id: 'number', label: 'A number', test: p => /[0-9]/.test(p) },
  { id: 'special', label: 'A special character', test: p => /[^A-Za-z0-9]/.test(p) },
  { id: 'nospace', label: 'No spaces', test: p => !/\s/.test(p) },
];

export function evaluatePasswordRules(password: string): { rule: PasswordRule; met: boolean }[] {
  return PASSWORD_RULES.map(rule => ({ rule, met: rule.test(password ?? '') }));
}

/** Angular validator – use on sign-up / new-password fields only (not on login). */
export function passwordPolicyValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value ?? '') as string;
  if (!value) return null;
  const failed = PASSWORD_RULES.filter(r => !r.test(value)).map(r => r.label);
  return failed.length ? { passwordPolicy: failed } : null;
}
